import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, of, tap } from 'rxjs';
import { CommonService } from '../../services/common.service';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { QueueService } from '../../services/queue.service';
import * as CommonActions from './actions';
import { ConnectionsService } from '../../services/connections.service';
import { MessageTemplatesService } from '../../services/message-templates.service';
import { QuickAnswerService } from '../../services/quick-answer.service';
import { EndpointsMaintenanceService } from '../../services/endpoints-maintenance.service';
import { EmailsMaintenanceService } from '../../services/emails-maintenance.service';
import { OptionsMaintenanceService } from '../../services/options-maintenance.service';
import { IntegrationsService } from '../../services/integrations.service';
import { AuthSessionService } from '../../services/auth-session.service';
import { SnackbarService } from '../services/snackbar.service';

@Injectable()
export class CommonEffects {
  private actions$ = inject(Actions);
  private snackbar = inject(SnackbarService);
  private commonService = inject(CommonService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private cookieService = inject(SsrCookieService);
  private platformId = inject(PLATFORM_ID);
  private queueService = inject(QueueService);
  private connectionsService = inject(ConnectionsService);
  private messageTemplatesService = inject(MessageTemplatesService);
  private quickAnswerService = inject(QuickAnswerService);
  private endpointService = inject(EndpointsMaintenanceService);
  private emailOptionsService = inject(EmailsMaintenanceService);
  private optionsMaintenanceService = inject(OptionsMaintenanceService);
  private integrationsService = inject(IntegrationsService);
  private authSessionService = inject(AuthSessionService);

  httpError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CommonActions.httpError),
        tap((action) => {
          if (!isPlatformBrowser(this.platformId)) {
            return;
          }
          if (action.error.status == 401) {
            this.snackbar.error(
              'No tienes permisos para realizar esta operación',
            );
            return;
          }

          if (
            action.error.error?.code ===
            'CHATBOT_CONNECTION_ALREADY_ASSIGNED'
          ) {
            this.snackbar.error(action.error.error.message);
            return;
          }

          this.snackbar.error('Ocurrió un error al procesar la solicitud');
        }),
      ),
    { dispatch: false },
  );

  getRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getRoles),
      exhaustMap((action) =>
        this.commonService.getRoles().pipe(
          map((roles) => CommonActions.getRolesSuccess({ roles })),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  checkAuthStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.checkAuthStatus),
      exhaustMap((action) =>
        this.authService.isAuthenticated().pipe(
          map((response) => CommonActions.checkAuthStatusSuccess(response)),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.logout),
      exhaustMap((action) =>
        this.authService.logout().pipe(
          map((response) => CommonActions.logoutSuccess()),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CommonActions.logoutSuccess),
        tap((action) => {
          this.authSessionService.clear();
          this.cookieService.delete('connect.sid');
          this.router.navigate(['/login']);
        }),
      ),
    { dispatch: false },
  );

  getQueues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getQueues),
      exhaustMap((action) =>
        this.queueService.getQueues().pipe(
          map((queues) => CommonActions.getQueuesSuccess({ queues })),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  getTicketStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getTicketStatuses),
      exhaustMap((action) =>
        this.commonService.getTicketStatuses().pipe(
          map((ticketStatuses) =>
            CommonActions.getTicketStatusesSuccess({ ticketStatuses }),
          ),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  getConnections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getConnections),
      exhaustMap((action) =>
        this.connectionsService.getConnections().pipe(
          map((connections) =>
            CommonActions.getConnectionsSuccess({ connections }),
          ),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  getTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getTemplates),
      exhaustMap((action) =>
        this.messageTemplatesService.getTemplates(action.wabaId).pipe(
          map((templates) => CommonActions.getTemplatesSuccess({ templates })),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  getTemplatesByQueue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getTemplatesByQueue),
      exhaustMap((action) =>
        this.messageTemplatesService.getTemplatesByQueue(action.queueId).pipe(
          map((templates) =>
            CommonActions.getTemplatesByQueueSuccess({ templates }),
          ),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  getQAns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getQuickAnswers),
      exhaustMap((action) =>
        this.quickAnswerService.getAllQAns().pipe(
          map((quick_answers) =>
            CommonActions.getQuickAnswersSuccess({ quick_answers }),
          ),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  getEndpoints$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getEndpoints),
      exhaustMap((action) =>
        this.endpointService.getEndpoints().pipe(
          map((endpoints) => CommonActions.getEndpointsSuccess({ endpoints })),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  getEmailOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getMailableOptions),
      exhaustMap((action) =>
        this.emailOptionsService.getEmailOptions().pipe(
          map((emails) => CommonActions.getMailableOptionsSuccess({ emails })),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  getTenant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getTenant),
      exhaustMap((action) =>
        this.authService.getSessionTenant().pipe(
          map((tenant) => CommonActions.getTenantSuccess({ tenant })),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  getIntegrations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getIntegrations),
      exhaustMap(() =>
        this.integrationsService.getIntegrations().pipe(
          map((integrations) =>
            CommonActions.getIntegrationsSuccess({ integrations }),
          ),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  getChatbotStepTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getChatbotStepTypes),
      exhaustMap((action) =>
        this.commonService.getChatbotStepTypes().pipe(
          map((stepTypes) =>
            CommonActions.getChatbotStepTypesSuccess({ stepTypes }),
          ),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  getChatbotIntegrations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getChatbotIntegrations),
      exhaustMap((action) =>
        this.commonService.getChatbotIntegrations().pipe(
          map((chatbotIntegrations) =>
            CommonActions.getChatbotIntegrationsSuccess({
              chatbotIntegrations,
            }),
          ),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  getNlpProviders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getNlpProviders),
      exhaustMap((action) =>
        this.commonService.getNlpProviders().pipe(
          map((nlpProviders) =>
            CommonActions.getNlpProvidersSuccess({ nlpProviders }),
          ),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );

  getNlpEntities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getNlpEntities),
      exhaustMap((action) =>
        this.commonService.getNlpEntities().pipe(
          map((nlpEntities) =>
            CommonActions.getNlpEntitiesSuccess({ nlpEntities }),
          ),
          catchError((error) => of(CommonActions.httpError({ error }))),
        ),
      ),
    ),
  );
}
